/**
 * EmailUsCard: the one way to reach Wildy from the public site. We chose plain email over forms
 * so every first contact is a real conversation in our inbox (see design-system/README.md › Forms).
 * The button opens the visitor's mail app with the subject (and an optional starter body) filled in;
 * "Copy" covers computers with no mail app set up, where mailto links do nothing.
 */

import { useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicContact } from '@/config/publicContact';

export function EmailUsCard({
  subject, body, buttonLabel, copyLabel, copiedLabel, note,
}: {
  subject: string;
  body?: string;
  buttonLabel: string;
  copyLabel: string;
  copiedLabel: string;
  /** One short line under the address, e.g. what to include. */
  note?: string;
}) {
  const email = publicContact.generalEmail;
  const [copied, setCopied] = useState(false);
  const params = [`subject=${encodeURIComponent(subject)}`, body && `body=${encodeURIComponent(body)}`].filter(Boolean).join('&');
  const href = `mailto:${email}?${params}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the address is visible and selectable anyway */ }
  };

  return (
    <div className="rounded-lg border border-brand-200 bg-white p-6 sm:p-8 shadow-sm text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-primary">
        <Mail className="w-6 h-6" aria-hidden />
      </span>
      <a href={href} className="mt-4 block text-2xl sm:text-3xl font-bold text-brand-900 tracking-tight break-all hover:text-primary hover:underline underline-offset-4">
        {email}
      </a>
      {note && <p className="mt-3 text-slate-600 leading-relaxed max-w-md mx-auto">{note}</p>}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button asChild size="lg" className="w-full sm:w-auto">
          <a href={href}>
            <Mail className="w-4 h-4 mr-2" aria-hidden />
            {buttonLabel}
          </a>
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={copy}
          className="w-full sm:w-auto border-primary text-primary hover:bg-brand-50 hover:text-brand-700">
          {copied ? <Check className="w-4 h-4 mr-2" aria-hidden /> : <Copy className="w-4 h-4 mr-2" aria-hidden />}
          <span aria-live="polite">{copied ? copiedLabel : copyLabel}</span>
        </Button>
      </div>
    </div>
  );
}
